import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, Play, Pause, SkipBack, SkipForward, ChevronDown, ChevronUp } from 'lucide-react';
import { useCourse } from '../hooks/useCourse';
import { useAuth } from '../hooks/useAuth';
import { logActivity } from '../lib/supabase';
import { Song, SongLine } from '../types';

function formatTime(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${s.toString().padStart(2, '0')}`;
}

function highlightVocab(text: string, highlights: string[]): React.ReactNode {
  if (!highlights || highlights.length === 0) return text;

  // Sort by length descending so longer phrases match first
  const sorted = [...highlights].sort((a, b) => b.length - a.length);
  const parts: React.ReactNode[] = [];
  let remaining = text;
  let key = 0;

  while (remaining.length > 0) {
    let found = false;
    const lowerRemaining = remaining.toLowerCase();

    for (const hl of sorted) {
      const idx = lowerRemaining.indexOf(hl.toLowerCase());
      if (idx !== -1) {
        if (idx > 0) {
          parts.push(remaining.substring(0, idx));
        }
        parts.push(
          <span key={key++} className="vocab-highlight">
            {remaining.substring(idx, idx + hl.length)}
          </span>
        );
        remaining = remaining.substring(idx + hl.length);
        found = true;
        break;
      }
    }

    if (!found) {
      parts.push(remaining);
      remaining = '';
    }
  }

  return <>{parts}</>;
}

export function SongLearn() {
  const { courseId, songId } = useParams<{ courseId: string; songId: string }>();
  const { course, isLoading } = useCourse(courseId || '');
  const { user } = useAuth();

  const audioRef = useRef<HTMLAudioElement>(null);
  const lyricsRef = useRef<HTMLDivElement>(null);
  const activeLineRef = useRef<HTMLDivElement>(null);
  const listenStartRef = useRef<number>(0);
  const loggedRef = useRef(false);

  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [activeLineIdx, setActiveLineIdx] = useState(-1);
  const [showVocab, setShowVocab] = useState(false);
  const [audioError, setAudioError] = useState(false);

  // Find the song
  const song: Song | undefined = course?.songs?.find(s => s.id === songId);

  // Find active line based on currentTime
  const findActiveLine = useCallback((time: number): number => {
    if (!song) return -1;
    for (let i = 0; i < song.lines.length; i++) {
      const line = song.lines[i];
      const effectiveEnd = line.start === line.end ? line.start + 0.5 : line.end;
      if (time >= line.start && time < effectiveEnd) {
        return i;
      }
    }
    // Check if we're between lines (instrumental section)
    for (let i = 0; i < song.lines.length - 1; i++) {
      const currentEnd = song.lines[i].end;
      const nextStart = song.lines[i + 1].start;
      if (time >= currentEnd && time < nextStart) {
        // Show the line we just passed
        return i;
      }
    }
    return -1;
  }, [song]);

  // Audio time update
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const onTimeUpdate = () => {
      const time = audio.currentTime;
      setCurrentTime(time);
      setActiveLineIdx(findActiveLine(time));
    };

    const onLoadedMetadata = () => {
      setDuration(audio.duration);
      setAudioError(false);
    };

    const onEnded = () => {
      setIsPlaying(false);
      // Log activity when song finishes
      if (user?.id && song && !loggedRef.current) {
        const listenedTime = Date.now() - listenStartRef.current;
        const listenedPct = Math.min(100, Math.round((audio.currentTime / audio.duration) * 100));
        if (listenedPct >= 80) {
          logActivity(user.id, 'song_listen', {
            course_id: courseId,
            song_id: song.id,
            song_title: song.title,
            listened_percentage: listenedPct,
            duration_seconds: Math.round(listenedTime / 1000),
          });
          loggedRef.current = true;
        }
      }
    };

    const onError = () => {
      setAudioError(true);
      setIsPlaying(false);
    };

    audio.addEventListener('timeupdate', onTimeUpdate);
    audio.addEventListener('loadedmetadata', onLoadedMetadata);
    audio.addEventListener('ended', onEnded);
    audio.addEventListener('error', onError);

    return () => {
      audio.removeEventListener('timeupdate', onTimeUpdate);
      audio.removeEventListener('loadedmetadata', onLoadedMetadata);
      audio.removeEventListener('ended', onEnded);
      audio.removeEventListener('error', onError);
    };
  }, [findActiveLine, user?.id, song, courseId]);

  // Scroll active line into view
  useEffect(() => {
    if (activeLineRef.current && isPlaying) {
      activeLineRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [activeLineIdx, isPlaying]);

  const togglePlay = () => {
    const audio = audioRef.current;
    if (!audio) return;

    if (isPlaying) {
      audio.pause();
    } else {
      if (audio.currentTime === 0) {
        listenStartRef.current = Date.now();
        loggedRef.current = false;
      }
      audio.play().catch(() => setAudioError(true));
    }
    setIsPlaying(!isPlaying);
  };

  const handleLineClick = (line: SongLine) => {
    const audio = audioRef.current;
    if (!audio) return;
    audio.currentTime = line.start;
    if (!isPlaying) {
      audio.play().catch(() => setAudioError(true));
      setIsPlaying(true);
    }
  };

  const skipPrev = () => {
    if (!song || !audioRef.current) return;
    const current = activeLineIdx > 0 ? activeLineIdx - 1 : 0;
    audioRef.current.currentTime = song.lines[current].start;
  };

  const skipNext = () => {
    if (!song || !audioRef.current) return;
    const next = activeLineIdx < song.lines.length - 1 ? activeLineIdx + 1 : song.lines.length - 1;
    audioRef.current.currentTime = song.lines[next].start;
  };

  const handleProgressClick = (e: React.MouseEvent<HTMLDivElement>) => {
    const audio = audioRef.current;
    if (!audio || !duration) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const pct = (e.clientX - rect.left) / rect.width;
    audio.currentTime = pct * duration;
  };

  // Get line style class
  const getLineClass = (idx: number): string => {
    if (idx === activeLineIdx) return 'lyric-line active';
    if (idx < activeLineIdx) return 'lyric-line previous';
    if (idx === activeLineIdx + 1) return 'lyric-line next';
    return 'lyric-line future';
  };

  // Collect unique vocab
  const vocabList = song ? Array.from(new Set(
    song.lines.flatMap(l => l.highlights).filter(h => h.length > 0)
  )).map(word => {
    const line = song.lines.find(l =>
      l.highlights.some(h => h.toLowerCase() === word.toLowerCase())
    );
    return { english: word, german: line?.german || '' };
  }) : [];

  if (isLoading) {
    return <div className="loading">Laden...</div>;
  }

  if (!song) {
    return (
      <div className="error-state">
        <p>Song nicht gefunden</p>
        <Link to={`/course/${courseId}`} className="back-link">
          <ArrowLeft size={18} />
          Zurueck zum Kurs
        </Link>
      </div>
    );
  }

  return (
    <div className="song-learn-container">
      <audio ref={audioRef} src={song.audioFile} preload="metadata" />

      {/* Header */}
      <Link to={`/course/${courseId}`} className="back-link">
        <ArrowLeft size={18} />
        Zurueck
      </Link>

      {/* Song info */}
      <div style={{
        textAlign: 'center',
        marginBottom: '20px',
      }}>
        <div style={{ fontSize: '40px', marginBottom: '8px' }}>{song.icon}</div>
        <h1 style={{ fontSize: '22px', fontWeight: 700, color: '#e8eaf6', marginBottom: '4px' }}>
          {song.title}
        </h1>
        <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: '14px' }}>
          {song.description}
        </p>
        <p style={{ color: 'rgba(255,255,255,0.4)', fontSize: '13px', marginTop: '4px' }}>
          {song.vocabCount} Vokabeln
        </p>
      </div>

      {/* Audio missing notice */}
      {audioError && (
        <div style={{
          background: 'rgba(255, 152, 0, 0.15)',
          border: '1px solid rgba(255, 152, 0, 0.3)',
          borderRadius: '12px',
          padding: '14px 16px',
          marginBottom: '16px',
          textAlign: 'center',
        }}>
          <p style={{ color: '#ff9800', fontSize: '14px', fontWeight: 600 }}>
            Audio-Datei wird noch hinzugefuegt
          </p>
          <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: '12px', marginTop: '4px' }}>
            Du kannst den Text trotzdem durchlesen
          </p>
        </div>
      )}

      {/* Lyrics area */}
      <div className="lyrics-container" ref={lyricsRef}>
        {song.lines.map((line, idx) => (
          <div key={idx}>
            <div
              ref={idx === activeLineIdx ? activeLineRef : undefined}
              className={getLineClass(idx)}
              onClick={() => handleLineClick(line)}
            >
              {idx === activeLineIdx
                ? highlightVocab(line.english, line.highlights)
                : line.english
              }
            </div>
            {idx === activeLineIdx && (
              <div className="lyric-translation">
                {line.german}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Vocab toggle */}
      <div style={{ textAlign: 'center', marginTop: '16px', marginBottom: '8px' }}>
        <button
          onClick={() => setShowVocab(!showVocab)}
          style={{
            background: 'rgba(255,255,255,0.08)',
            border: '1px solid rgba(255,255,255,0.15)',
            borderRadius: '10px',
            padding: '10px 20px',
            color: '#e8eaf6',
            fontSize: '14px',
            fontWeight: 600,
            cursor: 'pointer',
            display: 'inline-flex',
            alignItems: 'center',
            gap: '8px',
          }}
        >
          {showVocab ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
          {showVocab ? 'Vokabeln ausblenden' : 'Vokabeln anzeigen'}
        </button>
      </div>

      {showVocab && vocabList.length > 0 && (
        <div style={{
          background: 'rgba(255,255,255,0.05)',
          borderRadius: '12px',
          padding: '16px',
          marginBottom: '16px',
        }}>
          <h3 style={{ color: '#e91e63', fontSize: '14px', fontWeight: 700, marginBottom: '12px' }}>
            Vokabeln in diesem Song:
          </h3>
          {vocabList.map((v, i) => (
            <div key={i} style={{
              display: 'flex',
              justifyContent: 'space-between',
              padding: '6px 0',
              borderBottom: i < vocabList.length - 1 ? '1px solid rgba(255,255,255,0.06)' : 'none',
            }}>
              <span style={{ color: '#ffd54f', fontSize: '14px', fontWeight: 600 }}>{v.english}</span>
            </div>
          ))}
        </div>
      )}

      {/* Fixed Player */}
      <div className="song-player">
        {/* Progress bar */}
        <div className="song-progress-bar" onClick={handleProgressClick}>
          <div
            className="song-progress-fill"
            style={{ width: duration ? `${(currentTime / duration) * 100}%` : '0%' }}
          />
        </div>

        {/* Time */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          color: 'rgba(255,255,255,0.4)',
          fontSize: '12px',
          marginBottom: '8px',
        }}>
          <span>{formatTime(currentTime)}</span>
          <span>{formatTime(duration)}</span>
        </div>

        {/* Controls */}
        <div className="song-controls">
          <button className="song-skip-btn" onClick={skipPrev}>
            <SkipBack size={22} />
          </button>
          <button className="song-play-btn" onClick={togglePlay}>
            {isPlaying ? <Pause size={24} /> : <Play size={24} style={{ marginLeft: '2px' }} />}
          </button>
          <button className="song-skip-btn" onClick={skipNext}>
            <SkipForward size={22} />
          </button>
        </div>
      </div>
    </div>
  );
}
